// colDef/batchColDef.tsx
import {
  ActionDropdown,
  ActionDropdownContent,
  ActionDropdownTrigger,
  DropdownNode,
} from "@/components/ActionDropdown";
import { cn } from "@/lib/utils";
import { BatchStatus, BatchType } from "@/services/batch.service";
import { createColumnHelper } from "@tanstack/react-table";
import { MoreVertical, Pencil } from "lucide-react";
import { Link } from "react-router-dom";

const columnHelper = createColumnHelper<BatchType>();

export type BatchActionType = {
  id: string;
  name: string;
  action: "edit" | "delete";
};

export type BatchPermissions = {
  canUpdate: boolean;
  canDelete: boolean;
};

// ─── Status pill config ───────────────────────────────────────────────────────

const STATUS_CONFIG: Record<BatchStatus, { label: string; className: string }> =
  {
    ongoing: {
      label: "Ongoing",
      className: "bg-blue-100 text-blue-700",
    },
    completed: {
      label: "Completed",
      className: "bg-gray-100 text-gray-500",
    },
  };

export const createBatchColumns = (
  onActionClick: (payload: BatchActionType) => void,
  permissions: BatchPermissions = { canUpdate: true, canDelete: true },
  programDeptCodeMap: Record<string, string> = {}
) => [
  // Batch Name
  columnHelper.accessor("name", {
    header: "Batch Name",
    cell: (info) => (
      <Link
        to={`/batches/${info.row.original.id}`}
        className="text-sm font-medium hover:underline"
      >
        {info.getValue()}
      </Link>
    ),
  }),

  // Department Code — differentiates batches that share the same year range.
  // Batches reference a department only via degree_program_id.
  columnHelper.accessor("degree_program_id", {
    id: "deptCode",
    header: "Dept Code",
    cell: (info) => {
      const code = programDeptCodeMap[info.getValue()];
      return (
        <span className="rounded-md bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-700">
          {code ?? "—"}
        </span>
      );
    },
  }),

  // Start Year
  columnHelper.accessor("start_year", {
    header: "Start Year",
    cell: (info) => <span className="text-sm">{info.getValue()}</span>,
  }),

  // End Year
  columnHelper.accessor("end_year", {
    header: "End Year",
    cell: (info) => <span className="text-sm">{info.getValue()}</span>,
  }),

  // Current Semester
  columnHelper.accessor("current_semester", {
    header: "Semester",
    cell: (info) => (
      <span className="text-sm font-medium">Sem {info.getValue()}</span>
    ),
  }),

  // Status pill
  columnHelper.accessor("status", {
    header: "Status",
    cell: (info) => {
      const cfg = STATUS_CONFIG[info.getValue()] ?? {
        label: info.getValue(),
        className: "bg-gray-100 text-gray-500",
      };
      return (
        <span
          className={cn(
            "whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold",
            cfg.className
          )}
        >
          {cfg.label}
        </span>
      );
    },
  }),

  // 3-dot action dropdown
  ...(permissions.canUpdate || permissions.canDelete
    ? [
        columnHelper.display({
          id: "actions",
          header: "",
          cell: (info) => {
            const data = info.row.original;

            const childrenNodes: DropdownNode[] = [];

            if (permissions.canUpdate) {
              childrenNodes.push({
                type: "item",
                label: "Edit Batch",
                icon: <Pencil size={14} />,
                onSelect: () =>
                  onActionClick({
                    id: data.id,
                    name: data.name,
                    action: "edit",
                  }),
              });
            }

            // if (permissions.canDelete) {
            //   childrenNodes.push({
            //     type: "item",
            //     label: "Delete Batch",
            //     icon: <Trash2 size={14} />,
            //     className: "text-red-600 focus:text-red-600",
            //     iconClass: "text-red-600",
            //     onSelect: () =>
            //       onActionClick({ id: data.id, name: data.name, action: "delete" }),
            //   });
            // }

            if (childrenNodes.length === 0) return null;

            const dropdownNodes: DropdownNode[] = [
              { type: "group", children: childrenNodes },
            ];

            return (
              <ActionDropdown>
                <ActionDropdownTrigger
                  triggerContent={<MoreVertical size={16} />}
                  buttonVariant="ghost"
                  buttonSize="icon"
                  className={{ buttonClass: "h-8 w-8 p-0" }}
                />
                <ActionDropdownContent
                  dropdownShape={dropdownNodes}
                  align="end"
                />
              </ActionDropdown>
            );
          },
        }),
      ]
    : []),
];
